import { CaretDownIcon, CaretUpIcon, HandbagIcon, StackIcon, TrashIcon } from "@phosphor-icons/react/dist/ssr";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Badge } from "@/core/shared/v2/ui/badge";
import { Button } from "@/core/shared/v2/ui/button";
import { formatPrice } from "@/core/utils/format-price";
import { useCartState } from "../../store/use-cart-state";
import { purchaseCatalogRowKey } from "../../utils/purchase-catalog-line";

interface SideCartProps {
  ctaText: string;
  ctaSize?: "lg" | "xl";
  showPaymentIcons?: boolean;
  onNextStep: (...args: never[]) => void;
  ctaLoading?: boolean;
  sticky?: boolean;
  appendedContent?: React.ReactNode;
  isCtaDisabled?: boolean;
  // Hide the cart on mobile only (max-lg breakpoint). Used by the F&F card
  // checkout flow to free up screen real estate while the user is editing
  // the patient/customer card — desktop keeps the cart visible in the
  // sidebar column.
  isHidden?: boolean;
}

export function SideCart({
  ctaText,
  ctaSize = "lg",
  showPaymentIcons = false,
  onNextStep,
  ctaLoading = false,
  sticky = true,
  appendedContent,
  isCtaDisabled = false,
  isHidden = false,
}: SideCartProps) {
  const { products, getTotalPrice, getTotalProductsCount, quantityModificationEnabled, updateProductQuantity } =
    useCartState();
  const totalPrice = getTotalPrice();
  const { t } = useTranslation();
  const hasProducts = products.length > 0;
  const totalProductsCount = getTotalProductsCount();

  const blockedAnimationIds = useRef<Set<string>>(new Set(products.map((p) => purchaseCatalogRowKey(p))));
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => setIsMobile(window.innerWidth < 1024);
    checkIsMobile();
    window.addEventListener("resize", checkIsMobile);
    return () => window.removeEventListener("resize", checkIsMobile);
  }, []);

  useEffect(() => {
    const currentIds = new Set(products.map((p) => purchaseCatalogRowKey(p)));
    blockedAnimationIds.current.forEach((id) => {
      if (!currentIds.has(id)) {
        blockedAnimationIds.current.delete(id);
      }
    });
  }, [products]);

  const paymentIcons = useMemo(
    () => [
      {
        src: "https://upacjenta.pl/next-assets/payments-icons/payu-icon.png",
        alt: t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.PAYMENT_ICONS.PAYU"),
        width: 46,
        height: 22,
      },
      {
        src: "https://upacjenta.pl/next-assets/payments-icons/visa-icon.png",
        alt: t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.PAYMENT_ICONS.VISA"),
        width: 44,
        height: 18,
      },
      {
        src: "https://upacjenta.pl/next-assets/payments-icons/mastercard-icon.png",
        alt: t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.PAYMENT_ICONS.MASTERCARD"),
        width: 29,
        height: 23,
      },
      {
        src: "https://upacjenta.pl/next-assets/payments-icons/blik-icon.png",
        alt: t("VOUCHERS_DISTRIBUTION.FREE_PACKAGES.PAYMENT_ICONS.BLIK"),
        width: 51,
        height: 32,
      },
    ],
    [t],
  );

  const content = (
    <div
      data-testid="side-cart"
      className={cn({
        "max-lg:hidden": !hasProducts || isHidden,
        "fixed right-0 bottom-0 left-0 z-50 shadow-[0_-4px_20px_rgba(0,0,0,0.1)] transition-all duration-300":
          hasProducts && sticky && isMobile,
        "max-lg:h-auto": hasProducts && sticky && isMobile && !isExpanded,
        "max-lg:h-[85vh]": hasProducts && sticky && isMobile && isExpanded,
      })}
    >
      <div
        className={cn([
          "flex flex-col gap-6",
          // Desktop styling (original)
          { "lg:sticky lg:top-32": sticky },
          // Mobile sticky styling overrides
          sticky && isMobile
            ? `gap-0 rounded-t-2xl bg-white ${isExpanded ? "px-4 pt-7 pb-4" : "p-4"} h-full`
            : "-mt-4 rounded-2xl p-6 shadow-[1px_10px_8px_0px_#00000008] max-lg:bg-white lg:bg-gradient-to-t lg:from-white lg:via-white lg:to-transparent",
          // Desktop gradient (keep original look for desktop)
          "max-lg:bg-white lg:-mt-4 lg:rounded-2xl lg:bg-gradient-to-t lg:from-white lg:via-white lg:to-transparent lg:p-6 lg:shadow-[1px_10px_8px_0px_#00000008]",
        ])}
      >
        <button
          type="button"
          className={cn("flex w-full select-none items-center gap-2 pb-2 text-left", {
            "relative cursor-pointer justify-between": sticky && isMobile,
          })}
          onClick={() => sticky && isMobile && setIsExpanded(!isExpanded)}
        >
          {sticky && isMobile && (
            <div
              className={cn(
                `absolute ${
                  isExpanded ? "-top-[42px]" : "-top-[30px]"
                } left-1/2 flex h-10 w-10 -translate-x-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-[0px_-3px_3px_#0000000a]`,
              )}
            >
              {isExpanded ? <CaretDownIcon size={16} /> : <CaretUpIcon size={16} />}
            </div>
          )}
          <div className="flex w-full items-center gap-2">
            <HandbagIcon size={32} />
            <h2 className="">{t("VOUCHERS_DISTRIBUTION.YOUR_CART")}</h2>
            {totalProductsCount > 0 && (
              <Badge
                key={totalProductsCount}
                className="zoom-in-50 flex h-6 w-6 animate-in items-center justify-center rounded-full p-0 duration-200"
                data-testid="side-cart-count"
              >
                <span className="text-xs">{totalProductsCount}</span>
              </Badge>
            )}
          </div>
          {sticky && (
            <div className="shrink-0 lg:hidden">
              <span className="font-bold text-lg">{totalPrice > 0 ? formatPrice(totalPrice) : "0 zł"}</span>
            </div>
          )}
        </button>

        {hasProducts ? (
          <>
            <div
              className={cn("flex-1", {
                hidden: sticky && isMobile && !isExpanded,
                "overflow-y-auto": sticky && isMobile && isExpanded,
              })}
            >
              {products.map((product) => (
                <div
                  key={purchaseCatalogRowKey(product)}
                  className={cn("group relative flex items-center gap-3", {
                    "slide-in-from-right-4 fade-in animate-in duration-300": !blockedAnimationIds.current.has(
                      purchaseCatalogRowKey(product),
                    ),
                  })}
                >
                  {quantityModificationEnabled && (
                    <button
                      type="button"
                      className="text-gray-400 transition-colors hover:text-destructive xl:hidden"
                      onClick={() => updateProductQuantity(product, (product.quantity || 1) - 1)}
                    >
                      <TrashIcon size={20} />
                    </button>
                  )}
                  <div className="flex flex-1 items-center justify-between py-2">
                    <div className="flex flex-col">
                      <span className="font-semibold">{product.name}</span>
                      <p className="inline-flex items-center text-sm max-lg:font-semibold">
                        <StackIcon size={14} />
                        &nbsp;
                        <span>
                          {t("VOUCHERS_DISTRIBUTION.VOUCHERS_LIST.BLOOD_TESTS_COUNT", {
                            count: product.bloodTests?.length || 0,
                          })}
                        </span>
                        &nbsp;
                      </p>
                      {product.quantity && product.quantity > 1 && (
                        <span className="text-gray-500 text-sm underline">
                          {t("COMMONS.VARIATIONS.UNITS.UNITS_COUNT", {
                            count: product.quantity,
                          })}
                        </span>
                      )}
                    </div>
                    <span>
                      {Number(product.grossPrice) > 0
                        ? formatPrice(Number(product.grossPrice) * (product.quantity || 1))
                        : "0 zł"}
                    </span>
                  </div>

                  {quantityModificationEnabled && (
                    <div className="absolute inset-0 hidden items-center justify-center gap-2 bg-gradient-to-r from-transparent via-primary-foreground to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 xl:flex">
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 rounded-full p-0 backdrop-blur-sm"
                        onClick={() => updateProductQuantity(product, (product.quantity || 1) - 1)}
                      >
                        -
                      </Button>
                      <span className="w-6 text-center font-semibold text-lg">{product.quantity || 1}</span>
                      <Button
                        size="sm"
                        variant="secondary"
                        className="h-8 w-8 rounded-full p-0 backdrop-blur-sm"
                        onClick={() => updateProductQuantity(product, (product.quantity || 1) + 1)}
                      >
                        +
                      </Button>
                    </div>
                  )}
                </div>
              ))}
              <div className="mt-4 flex items-center justify-between border-t pt-4 text-base">
                <span className="font-semibold">{t("VOUCHERS_DISTRIBUTION.TO_PAY")}</span>
                <span className="font-bold text-3xl">{totalPrice > 0 ? formatPrice(totalPrice) : "0 zł"}</span>
              </div>
            </div>

            <div className="flex flex-col gap-2 pt-2">
              <Button
                size={ctaSize}
                variant="primary"
                className="w-full"
                onClick={onNextStep}
                loading={ctaLoading}
                disabled={isCtaDisabled}
                data-testid="side-cart-cta"
              >
                {ctaText}
              </Button>
            </div>
          </>
        ) : (
          <div className="pointer-events-none flex select-none flex-col items-center justify-center rounded-3xl py-8">
            <p className="text-center opacity-20">{t("VOUCHERS_DISTRIBUTION.YOUR_CART_IS_EMPTY")}</p>
          </div>
        )}
      </div>
      {showPaymentIcons && (
        <div className="mt-3 flex items-center justify-center gap-4 md:mt-4">
          {paymentIcons.map(({ src, alt, width, height }) => (
            <img key={alt} src={src} alt={alt} width={width} height={height} />
          ))}
        </div>
      )}
      {appendedContent}
    </div>
  );

  if (sticky && isMobile && hasProducts) {
    return createPortal(content, document.body);
  }

  return content;
}
